/**
 * Split a callout's children into 1–6 equal-width columns (a row of column
 * blocks). Count 1 unwraps back to a vertical stack.
 *
 * `props.columns` (>= 2) is the persistent "keep balancing" mode. While it is
 * set, every commit re-packs the callout so column heights stay even as
 * content is added, removed, or edited.
 */

import { createBlock } from './blockUtils';
import { stripHtml } from './caretUtils';

export const CALLOUT_COLUMN_COUNTS = [1, 2, 3, 4, 5, 6];

const CHARS_PER_LINE = 40;

const isEmptyText = (block) =>
  block?.type === 'text' && !(block.content || '').trim() && !(block.children?.length);

const clampColumnCount = (value) => {
  const n = Math.round(Number(value) || 0);
  if (n < 2) return 0;
  return Math.min(6, n);
};

/** Stored auto-balance count, or 0 when the mode is off. */
export const readCalloutColumnsProp = (props) => clampColumnCount(props?.columns);

/** Current column count when the callout is a single row of columns; else 1. */
export const getCalloutColumnCount = (children = []) => {
  if (children.length !== 1 || children[0].type !== 'row') return 1;
  const cols = (children[0].children || []).filter((c) => c.type === 'column');
  if (cols.length < 2) return 1;
  if (cols.length !== (children[0].children || []).length) return 1;
  return Math.min(6, cols.length);
};

/** Active column count: stored mode, else a legacy one-off row of columns. */
export const resolveCalloutColumnCount = (block) =>
  readCalloutColumnsProp(block?.props) || getCalloutColumnCount(block?.children);

/**
 * Relative height of a block so packing can even columns by content, not
 * just by block count. Units are roughly "text lines".
 */
export const estimateBlockWeight = (block) => {
  if (!block) return 1;
  switch (block.type) {
    case 'divider':
      return 1;
    case 'image':
    case 'video':
      return 8;
    case 'table': {
      const rows = (block.props?.tableData?.rows?.length || 0) + 1;
      return Math.max(3, rows);
    }
    case 'code': {
      const lines = String(block.content || '').split('\n').length;
      return Math.max(2, lines);
    }
    case 'callout':
    case 'toggle':
    case 'choice':
    case 'choice-path':
    case 'row':
    case 'column': {
      const kids = (block.children || []).reduce(
        (sum, child) => sum + estimateBlockWeight(child),
        0
      );
      return Math.max(2, kids + (block.type === 'callout' ? 1 : 0));
    }
    default: {
      const text = stripHtml(block.content || '');
      const extraLines = (text.match(/\n/g) || []).length;
      return Math.max(1, extraLines + Math.ceil(Math.max(text.length, 1) / CHARS_PER_LINE));
    }
  }
};

/**
 * Lift callout children back to a flat stack. A lone row of columns is
 * unwrapped; empty placeholder columns (a single blank text block) are dropped
 * so adding then reducing columns doesn't inflate the stack with empties.
 */
export const flattenCalloutLayout = (children = []) => {
  if (!children.length) return [];

  const unwrapRow = (row) => {
    const out = [];
    for (const col of row.children || []) {
      const stack = col.type === 'column' ? col.children || [] : [col];
      const meaningful = stack.filter((b) => !isEmptyText(b));
      // Keep blanks that sit with real content; drop all-empty placeholder cols
      if (meaningful.length) out.push(...stack);
    }
    return out;
  };

  if (children.length === 1 && children[0].type === 'row') {
    return unwrapRow(children[0]);
  }

  const out = [];
  for (const child of children) {
    if (child.type === 'row') out.push(...unwrapRow(child));
    else out.push(child);
  }
  return out;
};

const sameStackIds = (cols, stacks) => {
  if (!cols || cols.length !== stacks.length) return false;
  return cols.every((col, i) => {
    if (col?.type !== 'column') return false;
    const kids = col.children || [];
    const stack = stacks[i];
    if (kids.length !== stack.length) return false;
    return kids.every((b, j) => b.id === stack[j].id);
  });
};

/**
 * Pack `items` into `count` contiguous columns, minimizing the tallest
 * column. Equal weights keep extras on the left (same as a count split).
 */
export const distributeBlocks = (items, count, weights) => {
  const n = Math.max(1, count);
  const cols = Array.from({ length: n }, () => []);
  if (!items.length) return cols;

  if (items.length <= n) {
    items.forEach((item, i) => {
      cols[i] = [item];
    });
    return cols;
  }

  const w =
    weights?.length === items.length
      ? weights.map((x) => Math.max(0.01, Number(x) || 1))
      : items.map(() => 1);

  const prefix = [0];
  for (const x of w) prefix.push(prefix[prefix.length - 1] + x);
  const range = (i, j) => prefix[j] - prefix[i];

  const INF = Number.POSITIVE_INFINITY;
  const dp = Array.from({ length: items.length + 1 }, () => Array(n + 1).fill(INF));
  const cut = Array.from({ length: items.length + 1 }, () => Array(n + 1).fill(0));
  dp[0][0] = 0;

  for (let i = 1; i <= items.length; i += 1) {
    for (let c = 1; c <= Math.min(n, i); c += 1) {
      for (let t = c - 1; t < i; t += 1) {
        if (dp[t][c - 1] === INF) continue;
        const load = Math.max(dp[t][c - 1], range(t, i));
        // Later split on a tie → shorter last column → extras stay left
        if (load <= dp[i][c]) {
          dp[i][c] = load;
          cut[i][c] = t;
        }
      }
    }
  }

  let i = items.length;
  let c = n;
  const slices = [];
  while (c > 0) {
    const t = cut[i][c];
    slices.push([t, i]);
    i = t;
    c -= 1;
  }
  slices.reverse();
  slices.forEach(([start, end], idx) => {
    cols[idx] = items.slice(start, end);
  });
  return cols;
};

const wrapStacks = (stacks, existingRow) => {
  const existingCols = existingRow?.type === 'row' ? existingRow.children || [] : [];
  const columns = stacks.map((stack, i) => {
    const prev = existingCols[i];
    let children = stack;
    if (!children.length) {
      children =
        prev?.type === 'column' &&
        prev.children?.length === 1 &&
        isEmptyText(prev.children[0])
          ? prev.children
          : [createBlock('text')];
    }
    if (prev?.type === 'column') {
      if (
        prev.children.length === children.length &&
        prev.children.every((b, j) => b === children[j])
      ) {
        return prev;
      }
      return { ...prev, children };
    }
    return { ...createBlock('column'), children };
  });

  if (existingRow?.type === 'row') {
    if (
      existingRow.children.length === columns.length &&
      existingRow.children.every((col, i) => col === columns[i])
    ) {
      return existingRow;
    }
    return { ...existingRow, children: columns };
  }
  return { ...createBlock('row'), children: columns };
};

/** Rebuild a callout's children as `count` content-balanced columns. */
export const setCalloutColumnCount = (children, count) => {
  const n = Math.max(1, Math.min(6, Math.round(Number(count) || 1)));
  const items = flattenCalloutLayout(children);
  if (n === 1) return items.length ? items : [createBlock('text')];

  const stacks = distributeBlocks(
    items,
    n,
    items.map((block) => estimateBlockWeight(block))
  );
  const existingRow = children.length === 1 && children[0].type === 'row' ? children[0] : null;
  return [wrapStacks(stacks, existingRow)];
};

/**
 * If this callout is in columns mode, pack children by content weight.
 * Returns the same object when the layout is already even.
 */
export const rebalanceCallout = (block) => {
  if (!block || block.type !== 'callout') return block;
  const n = resolveCalloutColumnCount(block);
  if (n < 2) return block;

  const items = flattenCalloutLayout(block.children);
  const stacks = distributeBlocks(
    items,
    n,
    items.map((child) => estimateBlockWeight(child))
  );

  const existingRow =
    block.children?.length === 1 && block.children[0].type === 'row'
      ? block.children[0]
      : null;
  const alreadyPacked = sameStackIds(existingRow?.children, stacks);
  const modeStored = readCalloutColumnsProp(block.props) === n;

  if (alreadyPacked && modeStored) return block;

  const row = alreadyPacked ? existingRow : wrapStacks(stacks, existingRow);
  const props = modeStored ? block.props : { ...(block.props || {}), columns: n };
  if (row === existingRow && props === block.props && alreadyPacked) return block;
  return { ...block, props, children: [row] };
};

/** Walk the tree and re-pack every callout that has columns mode on. */
export const rebalanceCalloutColumnsInTree = (blocks) => {
  if (!blocks?.length) return blocks;
  let changed = false;
  const next = blocks.map((block) => {
    let current = block;
    if (current.children?.length) {
      const children = rebalanceCalloutColumnsInTree(current.children);
      if (children !== current.children) {
        current = { ...current, children };
        changed = true;
      }
    }
    if (current.type === 'callout') {
      const balanced = rebalanceCallout(current);
      if (balanced !== current) {
        current = balanced;
        changed = true;
      }
    }
    return current;
  });
  return changed ? next : blocks;
};
